import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import type { PackAssignment } from '@goelectric/shared-types';
import { asignacionesApi } from '../api/asignacionesApi';
import { usePackAssignments } from '../hooks/usePackAssignments';
import { describeAssignmentError } from '../utils/errors';
import {
  formatConsumedDetail,
  formatCop,
  formatRemainingKwh,
  formatVigencia,
} from '../utils/format';
import { PackAssignmentStatusBadge } from './AssignmentStatusBadge';
import { AssignPackModal } from './AssignPackModal';
import { ReceiptsModal, type ReceiptsTarget } from './ReceiptsModal';

export interface PackAssignmentsPanelProps {
  customerId: string;
  customerName?: string;
  canWrite: boolean;
  /** Refresca la ficha del cliente tras asignar/cancelar. */
  onChanged?: () => void;
}

/**
 * Sección de **Packs de energía** de la ficha del cliente (US-2.4, TG-53).
 *
 * Muestra las asignaciones vigentes y el histórico. `SUPER_ADMIN` puede asignar un
 * pack del catálogo activo vigente, cancelar con confirmación destructiva y gestionar
 * comprobantes; el resto de roles consulta en modo lectura.
 */
export function PackAssignmentsPanel({
  customerId,
  customerName,
  canWrite,
  onChanged,
}: PackAssignmentsPanelProps) {
  const { message } = App.useApp();
  const packs = usePackAssignments(customerId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [receiptsTarget, setReceiptsTarget] = useState<ReceiptsTarget | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // El histórico (`status=ALL`) incluye las activas; el hook ya las excluye por id.
  const hasHistory = packs.historyMeta.total > packs.activeMeta.total;

  const cancel = async (assignment: PackAssignment) => {
    setCancellingId(assignment.id);
    try {
      await asignacionesApi.cancelPackAssignment(customerId, assignment.id);
      message.success('Asignación de pack cancelada.');
      packs.refresh();
      onChanged?.();
    } catch (caught) {
      message.error(describeAssignmentError(caught, 'No se pudo cancelar la asignación.'));
    } finally {
      setCancellingId(null);
    }
  };

  const openReceipts = (assignment: PackAssignment) => {
    setReceiptsTarget({
      type: 'pack',
      assignmentId: assignment.id,
      label: `${assignment.packName} (${assignment.id})`,
    });
  };

  const activeColumns: ColumnsType<PackAssignment> = [
    {
      title: 'Pack',
      key: 'pack',
      render: (_, assignment) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{assignment.packName}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}>
            {assignment.id}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Saldo (kWh)',
      key: 'kwh',
      width: 200,
      render: (_, assignment) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>
            {formatRemainingKwh(assignment.kwhTotal, assignment.kwhConsumed)}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatConsumedDetail(assignment.kwhTotal, assignment.kwhConsumed)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Vigencia',
      key: 'validity',
      width: 250,
      render: (_, assignment) => formatVigencia(assignment.validFrom, assignment.validTo),
    },
    {
      title: 'Precio',
      key: 'price',
      width: 140,
      align: 'right',
      render: (_, assignment) => formatCop(assignment.priceCop, assignment.currency || 'COP'),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 140,
      render: (_, assignment) => <PackAssignmentStatusBadge status={assignment.status} />,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 230,
      align: 'right',
      render: (_, assignment) => (
        <Space size={4}>
          <Button
            size="small"
            aria-label={`Comprobantes de ${assignment.packName}`}
            onClick={() => openReceipts(assignment)}
          >
            Comprobantes
          </Button>
          {canWrite && (
            <Popconfirm
              title="Cancelar asignación de pack"
              description="El pack saldrá de los packs activos del cliente y quedará en el historial como cancelado. Esta acción no se puede deshacer."
              okText="Cancelar asignación"
              okButtonProps={{ danger: true, loading: cancellingId === assignment.id }}
              cancelText="Volver"
              onConfirm={() => void cancel(assignment)}
            >
              <Button
                size="small"
                danger
                aria-label={`Cancelar ${assignment.packName}`}
              >
                Cancelar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const historyColumns: ColumnsType<PackAssignment> = [
    {
      title: 'Pack',
      key: 'pack',
      render: (_, assignment) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{assignment.packName}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}>
            {assignment.id}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Vigencia',
      key: 'validity',
      width: 250,
      render: (_, assignment) => formatVigencia(assignment.validFrom, assignment.validTo),
    },
    {
      title: 'Precio',
      key: 'price',
      width: 140,
      align: 'right',
      render: (_, assignment) => formatCop(assignment.priceCop, assignment.currency || 'COP'),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 140,
      render: (_, assignment) => <PackAssignmentStatusBadge status={assignment.status} />,
    },
    {
      title: 'Comprobantes',
      key: 'receipts',
      width: 150,
      align: 'right',
      render: (_, assignment) => (
        <Button
          size="small"
          aria-label={`Comprobantes de ${assignment.packName}`}
          onClick={() => openReceipts(assignment)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <section aria-labelledby="packs-asignados-title">
      <div className="clientes-page__header">
        <Typography.Title level={4} id="packs-asignados-title" style={{ margin: 0 }}>
          Packs de energía
        </Typography.Title>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAssignOpen(true)}
          >
            Asignar pack
          </Button>
        )}
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {packs.activeError ? (
          <Alert
            type="error"
            showIcon
            message="No se pudieron cargar los packs activos"
            description={packs.activeError}
            action={
              <Button size="small" onClick={packs.refresh}>
                Reintentar
              </Button>
            }
          />
        ) : packs.activeLoading && packs.active.length === 0 ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : packs.activeMeta.total === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="El cliente no tiene packs activos."
          >
            {canWrite && (
              <Button type="primary" onClick={() => setAssignOpen(true)}>
                Asignar el primer pack
              </Button>
            )}
          </Empty>
        ) : (
          <Table<PackAssignment>
            rowKey="id"
            size="middle"
            loading={packs.activeLoading}
            columns={activeColumns}
            dataSource={packs.active}
            scroll={{ x: 1100 }}
            aria-label="Packs activos del cliente"
            pagination={{
              current:
                Math.floor(packs.activeMeta.offset / Math.max(packs.activeMeta.limit, 1)) + 1,
              pageSize: packs.activeMeta.limit,
              total: packs.activeMeta.total,
              showSizeChanger: false,
              showTotal: (total) => `${total} packs activos`,
              onChange: (page) =>
                packs.setActivePage((page - 1) * packs.activeMeta.limit),
            }}
            locale={{ emptyText: 'No hay packs activos en esta página.' }}
          />
        )}

        {(hasHistory || packs.historyError) && (
          <div>
            <Typography.Title level={5}>Histórico de packs</Typography.Title>
            {packs.historyError ? (
              <Alert
                type="error"
                showIcon
                message="No se pudo cargar el histórico de packs"
                description={packs.historyError}
                action={
                  <Button size="small" onClick={packs.refresh}>
                    Reintentar
                  </Button>
                }
              />
            ) : (
              <Table<PackAssignment>
                rowKey="id"
                size="small"
                loading={packs.historyLoading}
                columns={historyColumns}
                dataSource={packs.history}
                scroll={{ x: 900 }}
                aria-label="Histórico de packs del cliente"
                pagination={{
                  current:
                    Math.floor(
                      packs.historyMeta.offset / Math.max(packs.historyMeta.limit, 1),
                    ) + 1,
                  pageSize: packs.historyMeta.limit,
                  total: packs.historyMeta.total,
                  showSizeChanger: false,
                  showTotal: (total) => `${total} asignaciones en el histórico`,
                  onChange: (page) =>
                    packs.setHistoryPage((page - 1) * packs.historyMeta.limit),
                }}
                locale={{ emptyText: 'Sin asignaciones históricas en esta página.' }}
              />
            )}
          </div>
        )}
      </Space>

      {canWrite && (
        <AssignPackModal
          open={assignOpen}
          customerId={customerId}
          customerName={customerName}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => {
            packs.refresh();
            onChanged?.();
          }}
        />
      )}

      <ReceiptsModal
        open={receiptsTarget !== null}
        customerId={customerId}
        target={receiptsTarget}
        canWrite={canWrite}
        onClose={() => setReceiptsTarget(null)}
      />
    </section>
  );
}
